import React from 'react';
import ReactSlider from 'react-slider';
import {
  FiFilter,
  FiX,
  FiChevronDown,
  FiChevronUp,
  FiTrendingUp,
  FiStar,
} from 'react-icons/fi';

const FiltersPanel = ({
  showFilter,
  setShowFilter,
  expandedFilters,
  toggleFilterSection,
  sortType,
  setSortType,
  categories,
  selectedMainCategory,
  setSelectedMainCategory,
  categoryItems,
  selectedSubItem,
  setSelectedSubItem,
  priceRange,
  setPriceRange,
  formatPrice,
  availableFilters,
  selectedColors,
  selectedMaterials,
  toggleFilterSelection,
  appliedFiltersCount,
  resetFilters,
  filteredProducts,
}) => {
  return (
    <div
      className={`w-full lg:w-72 xl:w-80 bg-white lg:bg-transparent fixed lg:static inset-0 z-40 overflow-y-auto transition-all duration-300 lg:mr-4 ${
        showFilter ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}
    >
      <div className="min-w-60 p-4 lg:p-0 h-full flex flex-col">
        {/* Mobile Header */}
        <div className="lg:hidden flex justify-between items-center border-b pb-4 mb-4">
          <h2 className="text-xl font-bold">Filters</h2>
          <button onClick={() => setShowFilter(false)} className="text-2xl">
            <FiX />
          </button>
        </div>

        {/* Filter Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Sort Section */}
          <div className="border border-gray-200 rounded-lg mb-4">
            <div
              className="flex justify-between items-center p-3 cursor-pointer"
              onClick={() => toggleFilterSection('sort')}
            >
              <h3 className="font-medium flex items-center gap-2">
                <FiTrendingUp /> Sort
              </h3>
              {expandedFilters.sort ? <FiChevronUp /> : <FiChevronDown />}
            </div>
            {expandedFilters.sort && (
              <div className="p-3 pt-0 space-y-2">
                {[
                  { value: 'relevant', label: 'Most Relevant' },
                  { value: 'newest', label: 'Newest' },
                  { value: 'price-low-high', label: 'Price: Low to High' },
                  { value: 'price-high-low', label: 'Price: High to Low' },
                  {
                    value: 'rating',
                    label: 'Top Rated',
                    icon: <FiStar className="text-yellow-500 ml-1" />,
                  },
                  { value: 'popularity', label: 'Most Popular' },
                  { value: 'a-z', label: 'A-Z' },
                  { value: 'z-a', label: 'Z-A' },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSortType(option.value)}
                    className={`w-full text-left p-2 text-sm rounded flex items-center ${
                      sortType === option.value
                        ? 'bg-gray-100 font-medium'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                    {option.icon}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Category Filter */}
          <div className="border border-gray-200 rounded-lg mb-4">
            <div
              className="flex justify-between items-center p-3 cursor-pointer"
              onClick={() => toggleFilterSection('category')}
            >
              <h3 className="font-medium">Categories</h3>
              {expandedFilters.category ? <FiChevronUp /> : <FiChevronDown />}
            </div>
            {expandedFilters.category && (
              <div className="p-3 pt-0 space-y-3">
                <select
                  value={selectedMainCategory}
                  onChange={(e) => setSelectedMainCategory(e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {selectedMainCategory && categoryItems.length > 0 && (
                  <select
                    value={selectedSubItem}
                    onChange={(e) => setSelectedSubItem(e.target.value)}
                    className="w-full p-2 border rounded text-sm"
                  >
                    <option value="">All Types</option>
                    {categoryItems.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          {/* Price Range Filter */}
          <div className="border border-gray-200 rounded-lg mb-4">
            <div
              className="flex justify-between items-center p-3 cursor-pointer"
              onClick={() => toggleFilterSection('price')}
            >
              <h3 className="font-medium">Price Range</h3>
              {expandedFilters.price ? <FiChevronUp /> : <FiChevronDown />}
            </div>
            {expandedFilters.price && (
              <div className="p-3 pt-0">
                <ReactSlider
                  className="horizontal-slider mb-4"
                  thumbClassName="slider-thumb"
                  trackClassName="slider-track"
                  value={priceRange}
                  onChange={setPriceRange}
                  min={0}
                  max={10000}
                  step={100}
                />
                <div className="flex justify-between text-sm">
                  <span>{formatPrice(priceRange[0])}</span>
                  <span>{formatPrice(priceRange[1])}</span>
                </div>
              </div>
            )}
          </div>

          {/* Color Filter */}
          <div className="border border-gray-200 rounded-lg mb-4">
            <div
              className="flex justify-between items-center p-3 cursor-pointer"
              onClick={() => toggleFilterSection('color')}
            >
              <h3 className="font-medium">Colors</h3>
              {expandedFilters.color ? <FiChevronUp /> : <FiChevronDown />}
            </div>
            {expandedFilters.color && (
              <div className="p-3 pt-0">
                <div className="grid grid-cols-4 gap-2">
                  {availableFilters.colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => toggleFilterSelection('color', color)}
                      className={`flex flex-col items-center p-1 rounded ${
                        selectedColors.includes(color)
                          ? 'ring-2 ring-black'
                          : 'border border-gray-200'
                      }`}
                    >
                      <div
                        className="w-5 h-5 rounded-full"
                        style={{ backgroundColor: color.toLowerCase() }}
                      />
                      <span className="text-xs mt-1 truncate w-full">
                        {color}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Material Filter */}
          <div className="border border-gray-200 rounded-lg mb-4">
            <div
              className="flex justify-between items-center p-3 cursor-pointer"
              onClick={() => toggleFilterSection('material')}
            >
              <h3 className="font-medium">Materials</h3>
              {expandedFilters.material ? <FiChevronUp /> : <FiChevronDown />}
            </div>
            {expandedFilters.material && (
              <div className="p-3 pt-0">
                <div className="space-y-2">
                  {availableFilters.materials.map((material) => (
                    <button
                      key={material}
                      onClick={() => toggleFilterSelection('material', material)}
                      className={`w-full text-left p-2 text-sm rounded ${
                        selectedMaterials.includes(material)
                          ? 'bg-gray-100 font-medium'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {material}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter Actions */}
        <div className="border-t pt-4 mt-auto">
          {appliedFiltersCount > 0 && (
            <button
              onClick={resetFilters}
              className="w-full py-2 text-sm underline text-gray-600 hover:text-black mb-2"
            >
              Clear all filters ({appliedFiltersCount})
            </button>
          )}
          <button
            onClick={() => window.innerWidth < 768 && setShowFilter(false)}
            className="w-full py-3 bg-black text-white rounded-lg hover:bg-gray-800"
          >
            Show {filteredProducts.length} Products
          </button>
        </div>
      </div>
    </div>
  );
};

export default FiltersPanel;